/**
 * Informar o banco do caso que nasceu sem ele, na mesa (E177.F1, GUS-253 A1, ADR-0131 §2.3).
 *
 * Só a plataforma, e só preenche (aparece só no caso sem banco): o banco vale para o processo inteiro (todos os exames dele), vai
 * à trilha de cada exame e faz a prévia ser refeita — a página relê o caso depois de gravar. O
 * escritório requerente é opcional, entre os vinculados ao banco.
 *
 * E180.F3 (GUS-254 item 2): o passo "Informar o banco do caso" (o card do pacote e o próximo passo)
 * chega aqui num clique, pela âncora: o quadro rola para a vista e o foco vai ao primeiro campo.
 *
 * E184.F4 (GUS-145 item 7, mesa do GT-2026-0245): o foco ia ao "Gravar" — a lista de bancos ainda
 * carregava e o campo não existia. Agora a âncora espera o campo do banco e age a cada clique no
 * atalho (a navegação nova tem outra `key`, mesmo com o endereço igual).
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { EscolhaDaOrigem } from '@/components/Abertura/OrigemDoCaso';
import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import { useCurrentRole } from '@/lib/auth/useCurrentRole';
import { ANCORA_DO_BANCO } from '@/lib/proximoPasso';
import {
  informarOrigem,
  ORIGEM_VAZIA,
  OrigemError,
  type OrigemEscolhida,
  type OrigemErrorReason,
} from '@/lib/origem_do_caso';

const t = ptBR.exameEletronico.informarOBanco;

const ERRO: Record<OrigemErrorReason, string> = {
  banco_ja_informado: t.erroJaInformado,
  origem_invalida: ptBR.abertura.origem.erroInvalida,
  unknown: t.erro,
};

export interface InformarOBancoProps {
  caseId: string;
  /** O banco do caso como a página o leu: `null` = sem banco; `undefined` = não sabe (não mostra). */
  bancoDoCaso: string | null | undefined;
  onInformado: () => void;
}

export function InformarOBanco({
  caseId,
  bancoDoCaso,
  onInformado,
}: InformarOBancoProps): JSX.Element | null {
  const { isPlatform } = useCurrentRole();
  if (bancoDoCaso !== null || !isPlatform) return null;
  return <QuadroDoBanco caseId={caseId} onInformado={onInformado} />;
}

function QuadroDoBanco({
  caseId,
  onInformado,
}: Omit<InformarOBancoProps, 'bancoDoCaso'>): JSX.Element {
  const client = useApiClient();
  const [origem, setOrigem] = useState<OrigemEscolhida>(ORIGEM_VAZIA);
  const [tentou, setTentou] = useState(false);
  const [gravando, setGravando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const { quadro, campo } = useAncoraDoBanco();

  async function gravar(): Promise<void> {
    setTentou(true);
    setErro(null);
    if (origem.banco === null) return;
    setGravando(true);
    try {
      await informarOrigem(client, caseId, { banco: origem.banco, escritorio: origem.escritorio });
      onInformado();
    } catch (err) {
      setErro(ERRO[err instanceof OrigemError ? err.reason : 'unknown']);
      // Com o banco já gravado (outra pessoa, outra aba), a página relê e o quadro some.
      if (err instanceof OrigemError && err.reason === 'banco_ja_informado') onInformado();
    } finally {
      setGravando(false);
    }
  }

  return (
    <section
      ref={quadro}
      id={ANCORA_DO_BANCO}
      className="mt-sc-3 border border-warning-500 bg-surface px-sc-4 py-sc-3"
      data-testid="informar-o-banco"
      aria-labelledby="informar-o-banco-titulo"
    >
      <h2 id="informar-o-banco-titulo" className="text-body-md font-medium text-fg">
        {t.titulo}
      </h2>
      <p className="mb-sc-3 text-body-sm text-fg-muted">{t.explicacao}</p>
      <EscolhaDaOrigem
        client={client}
        valor={origem}
        onChange={setOrigem}
        invalido={tentou && origem.banco === null}
        disabled={gravando}
        campoDoBanco={campo}
      />
      <div className="mt-sc-3 flex items-center gap-sc-3">
        <Button
          type="button"
          disabled={gravando}
          onClick={() => void gravar()}
          data-testid="informar-o-banco-gravar"
        >
          {gravando ? t.gravando : t.gravar}
        </Button>
        {erro !== null ? (
          <p
            role="alert"
            className="text-caption text-danger-700"
            data-testid="informar-o-banco-erro"
          >
            {erro}
          </p>
        ) : null}
      </div>
    </section>
  );
}

interface AncoraDoBanco {
  quadro: React.RefObject<HTMLElement>;
  /** Ref de callback: o campo entra no estado quando a lista fica pronta e o efeito o alcança. */
  campo: (campo: HTMLSelectElement | null) => void;
}

/** Com a âncora do banco no endereço, o quadro vem à vista e o campo do banco recebe o foco. */
function useAncoraDoBanco(): AncoraDoBanco {
  const { hash, key } = useLocation();
  const quadro = useRef<HTMLElement>(null);
  const [campo, setCampo] = useState<HTMLSelectElement | null>(null);
  useEffect(() => {
    if (hash !== `#${ANCORA_DO_BANCO}` || campo === null) return;
    quadro.current?.scrollIntoView({ block: 'center' });
    campo.focus({ preventScroll: true });
  }, [hash, key, campo]);
  return { quadro, campo: setCampo };
}
